import { lazy } from 'react'

import { Engagement } from '@factorialco/factorial-one-react/icons/modules'
import { EngagementTabsDocument } from 'generated/resources/graphql'
import { MeetingsMeetingDataDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { type NavigationRoutesFn, buildModuleRoute } from 'modules/core/lib/react-router'

import { engagementInsightsRoutes } from 'modules/engagement_insights/routes'
import enpsRoutes from 'modules/enps/routes'
import surveysRoutes from 'modules/surveys/routes'

const RedirectToRoutePage = lazy(() => import('modules/engagement/pages/RedirectToRoutePage'))
const RootPage = lazy(() => import('modules/engagement/pages/RootPage'))
const PulseRootPage = lazy(() => import('modules/engagement/pages/pulse/RootPage'))
const RedirectPulse = lazy(() => import('modules/engagement/pages/RedirectPulse'))
const PulsePage = lazy(() => import('modules/engagement/pages/pulse'))
const AiReportPage = lazy(() => import('modules/engagement/pages/pulseAiReport'))
const OneOnOnesPage = lazy(() => import('modules/engagement/pages/OneOnOnes'))

const MeetingPageHeader = lazy(
  () => import('modules/meetings/pages/MeetingMeetPage/components/MeetingPageHeader')
)
const MeetingRootPage = lazy(() => import('modules/meetings/pages/MeetingRootPage'))
const MeetingMeetPage = lazy(() => import('modules/meetings/pages/MeetingMeetPage'))
const MeetingPrejoinRoomPage = lazy(() => import('modules/meetings/pages/MeetingPrejoinRoomPage'))
const MeetingRoomPage = lazy(() => import('modules/meetings/pages/MeetingRoomPage'))

export const engagementRoutes: NavigationRoutesFn = (navigation) => {
  return [
    buildModuleRoute({
      path: navigation.engagement.path,
      element: <RootPage />,
      handle: {
        module: 'engagement',
        moduleIcon: Engagement,
        moduleName: () => i18n.t('sidebar.engagement'),
        meta: ({ watchQuery, session }) => {
          return watchQuery({
            query: EngagementTabsDocument,
            variables: {
              companyId: session.company.id,
            },
          }).map(({ data }) => {
            const settingsItems: { label: string; href: string }[] = []
            const settingsManage = data?.engagement?._policies.settingsManage.can

            if (
              data?.surveys?._policies.sidebarSeeEngagementSurveysSubLevelItem.can === true &&
              data?.apiCore?._policies.accessSurveysCreateTemplates.can === true
            ) {
              settingsItems.push({
                label: i18n.t('surveys.index.title'),
                href: navigation.settings.surveys.fullPath(),
              })
            }

            const pulseFeature = data.features.featuresConnection?.edges.find(
              (feature) => feature.node.name === 'engagement_employee_pulse_questions'
            )?.node.enabled

            if (pulseFeature && settingsManage) {
              settingsItems.push({
                label: i18n.t('engagement.pulse.title'),
                href: navigation.settings.engagement.fullPath(),
              })
            }
            const upsellFeatureFlagEnabled =
              data.features.featuresConnection?.edges.find(
                (feature) => feature.node.name === 'engagement_upsell_flow'
              )?.node.enabled ?? false
            const upsellPolicyEnabled = data.upsell._policies.canRequestUpsell.can ?? false
            const upsellFlowEnabled = upsellFeatureFlagEnabled && upsellPolicyEnabled

            const surveysVisible =
              data?.surveys?._policies.sidebarSeeEngagementSurveysSubLevelItem.can
            const enpsVisible = data?.enps?._policies.sidebarSeeEngagementEnpsSubLevelItem.can
            const insightsVisible =
              data?.engagementInsights?._policies.sidebarSeeEngagementInsightsSubLevelItem.can
            const meetingsVisible = data?.meetings?._policies.seeMeetings.can

            return {
              title: i18n.t('sidebar.engagement'),
              settings: settingsItems.length > 0 ? { items: settingsItems } : undefined,
              moduleIcon: Engagement,
              subNavigation: [
                {
                  id: 'navigation.engagement.pulse.fullPath',
                  title: i18n.t('engagement.pulse.title'),
                  href: navigation.engagement.pulse.fullPath(),
                  isVisible: data?.engagement?._policies.seePulseDashboard.can,
                },
                {
                  id: 'navigation.engagement.surveys.fullPath',
                  title: i18n.t('surveys.index.title'),
                  href: navigation.engagement.surveys.fullPath(),
                  isVisible: upsellFlowEnabled || surveysVisible,
                  variant: upsellFlowEnabled && !surveysVisible ? 'upsell' : undefined,
                },
                {
                  id: 'navigation.engagement.enps.fullPath',
                  title: i18n.t('enps.index.title'),
                  href: navigation.engagement.enps.fullPath(),
                  isVisible: enpsVisible || upsellFlowEnabled,
                  variant: upsellFlowEnabled && !enpsVisible ? 'upsell' : undefined,
                },
                {
                  id: 'navigation.engagement.insights.fullPath',
                  title: i18n.t('surveys.insights'),
                  href: navigation.engagement.insights.fullPath(),
                  index: true,
                  isVisible: insightsVisible || upsellFlowEnabled,
                  variant: upsellFlowEnabled && !insightsVisible ? 'upsell' : undefined,
                },
                {
                  id: 'navigation.engagement.one_on_ones.fullPath',
                  title: i18n.t('engagement.one_on_ones.title'),
                  href: navigation.engagement.one_on_ones.fullPath(),
                  isVisible: meetingsVisible,
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <RedirectToRoutePage />,
        },
        ...engagementInsightsRoutes(navigation),
        {
          path: navigation.engagement.pulse.path,
          element: <PulseRootPage />,
          handle: {
            meta: ({ watchQuery, session, navigate }) => {
              return watchQuery({
                query: EngagementTabsDocument,
                variables: {
                  companyId: session.company.id,
                },
              }).map(({ data }) => {
                if (!data.engagement._policies.seePulseDashboard.can) {
                  navigate(navigation.engagement.fullPath())
                }

                const pulseAiReportFeature = data.features.featuresConnection?.edges.find(
                  (feature) => feature.node.name === 'engagement_employee_pulse_ai_report'
                )?.node.enabled

                return {
                  title: i18n.t('engagement.pulse.title'),
                  subNavigation: [
                    {
                      id: 'navigation.engagement.pulse.participation.fullPath',
                      title: i18n.t('engagement.pulse.participation.title'),
                      href: navigation.engagement.pulse.participation.fullPath(),
                    },
                    {
                      id: 'navigation.engagement.pulse.aiReport.fullPath',
                      title: i18n.t('engagement.pulse.pulse_ai_report.title'),
                      href: navigation.engagement.pulse.aiReport.fullPath(),
                      isVisible: pulseAiReportFeature,
                    },
                  ],
                }
              })
            },
          },
          children: [
            { path: '', element: <RedirectPulse />, index: true },
            { path: navigation.engagement.pulse.participation.path, element: <PulsePage /> },
            {
              path: navigation.engagement.pulse.aiReport.path,
              element: <AiReportPage />,
              handle: {
                meta: ({ watchQuery, session, navigate }) => {
                  return watchQuery({
                    query: EngagementTabsDocument,
                    variables: {
                      companyId: session.company.id,
                    },
                  }).map(({ data }) => {
                    if (
                      !data.features.featuresConnection?.edges.find(
                        (feature) => feature.node.name === 'engagement_employee_pulse_ai_report'
                      )?.node.enabled
                    ) {
                      navigate(navigation.engagement.pulse.participation.fullPath())
                    }

                    return {
                      title: i18n.t('engagement.pulse.pulse_ai_report.title'),
                    }
                  })
                },
              },
            },
          ],
        },
        ...surveysRoutes(navigation),
        ...enpsRoutes(navigation),
        {
          path: navigation.engagement.one_on_ones.path,
          handle: {
            meta: () => ({
              title: i18n.t('engagement.one_on_ones.title'),
            }),
          },
          children: [
            {
              index: true,
              element: <OneOnOnesPage />,
            },
            {
              path: navigation.engagement.one_on_ones.meeting.path,
              children: [
                {
                  path: navigation.engagement.one_on_ones.meeting.meetId.path,
                  element: <MeetingRootPage />,
                  handle: {
                    resource: true,
                    meta: ({ params: { id }, watchQuery }) => {
                      return watchQuery({
                        query: MeetingsMeetingDataDocument,
                        variables: {
                          id: Number(id),
                        },
                      }).map(({ data }) => {
                        const meeting = data?.meetings.meeting
                        const participants = meeting?.attendees
                        const organizer = meeting?.organizer

                        if (!meeting) {
                          return {
                            title: i18n.t('meetings.meeting.not_found'),
                          }
                        }

                        return {
                          title: meeting?.title ?? i18n.t('meetings.room.title'),
                          headerComponent: (
                            <MeetingPageHeader
                              meeting={meeting ?? undefined}
                              participants={participants ?? undefined}
                              organizer={organizer ?? undefined}
                            />
                          ),
                        }
                      })
                    },
                  },
                  children: [
                    {
                      index: true,
                      element: <MeetingMeetPage />,
                    },
                    {
                      path: navigation.engagement.one_on_ones.meeting.meetId.room.path,
                      element: <MeetingRoomPage />,
                      handle: {
                        resource: true,
                        meta: () => ({
                          title: i18n.t('meetings.room.title'),
                        }),
                      },
                    },
                    {
                      path: navigation.engagement.one_on_ones.meeting.meetId.prejoin.path,
                      element: <MeetingPrejoinRoomPage />,
                      handle: {
                        resource: true,
                        meta: () => ({
                          title: i18n.t('meetings.prejoin.title'),
                        }),
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    }),
  ]
}
